import React, { useCallback, useEffect, useRef, useState } from 'react';
import InteractiveRoute from './InteractiveRoute';
import { RefreshCw, X } from 'lucide-react';

const COLORS = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
const FALLBACK_POS = { lat: -6.2088, lng: 106.8456, speed: 0, altitude: 0 };
const BASE_URL = 'https://race.geekbike.cc';
const AUTH = btoa(`${process.env.REACT_APP_LOGIN_EMAIL}:${process.env.REACT_APP_LOGIN_PASSWORD}`);

const Screen = ({ children }) => (
  <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050a14' }}>
    <div style={{ textAlign: 'center' }}>{children}</div>
    <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
  </div>
);

export default function App() {
  const trackHistoryRef = useRef({});
  const devicesRef = useRef(null);

  const [isDark, setIsDark] = useState(true);
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiFetch = async (path) => {
    const res = await fetch(`${BASE_URL}${path}`, {
      headers: { 'Authorization': `Basic ${AUTH}` },
    });
    if (!res.ok) throw new Error(`Failed to fetch ${path} (${res.status})`);
    return res.json();
  };

  const fetchData = useCallback(async (refetchDevices = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!devicesRef.current || refetchDevices) {
        devicesRef.current = await apiFetch('/api/devices');
      }

      const devices = devicesRef.current;
      const posMap = {};
      const hasOnline = true;

      if (hasOnline) {
        const positions = await apiFetch('/api/positions');
        positions.forEach(p => { posMap[p.deviceId] = p; });
      }

      setTrackers(devices.map((device, i) => {
        const tid = `bike-${device.id}`;
        const pos = posMap[device.id];
        const hist = trackHistoryRef.current[tid] ??= [];
        const isOnline = true;

        if (isOnline && pos) {
          hist.push({
            id: hist.length + 1,
            lat: pos.latitude,
            lng: pos.longitude,
            elevation: pos.altitude || 0,
            distance: (pos.attributes?.totalDistance ?? 0) / 1000,
            time: new Date(pos.fixTime).toLocaleTimeString('id-ID'),
            speed: pos.speed || 0,
          });
          if (hist.length > 10) hist.shift();
        }

        const last = hist[hist.length - 1];
        const livePosition = isOnline && pos
          ? { lat: pos.latitude, lng: pos.longitude, speed: pos.speed || 0, altitude: pos.altitude || 0 }
          : last
            ? { lat: last.lat, lng: last.lng, speed: 0, altitude: last.elevation }
            : FALLBACK_POS;

        const totalDistance = isOnline && pos
          ? ((pos.attributes?.totalDistance ?? 0) / 1000).toFixed(2)
          : last ? last.distance.toFixed(2) : '0.00';

        return {
          id: tid,
          deviceId: device.id,
          name: device.name,
          color: COLORS[i % COLORS.length],
          status: device.status,
          livePosition,
          totalDistance,
          lastUpdate: pos?.serverTime || null,
        };
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData(true);
    const interval = setInterval(() => fetchData(false), 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading && !trackers.length)
    return (
      <Screen>
        <RefreshCw size={48} style={{ color: '#f59e0b', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#fff', marginTop: 16, fontFamily: 'monospace' }}>Loading tracker data...</p>
      </Screen>
    );

  if (error)
    return (
      <Screen>
        <X size={48} style={{ color: '#ef4444' }} />
        <p style={{ color: '#fff', margin: '16px 0', fontFamily: 'monospace' }}>Error: {error}</p>
        <button onClick={() => fetchData(true)}
          style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '8px 24px', borderRadius: 8, cursor: 'pointer', fontFamily: 'monospace' }}>
          Retry
        </button>
      </Screen>
    );

  return (
    <InteractiveRoute
      trackers={trackers}
      isDark={isDark}
      onToggleDark={() => setIsDark(d => !d)}
      onRefresh={() => fetchData(true)}
      loading={loading}
    />
  );
}
